<template>
  <div class="form-field form-field-text">
    <label :for="field.name">{{ label }}</label>
    <div class="input-phone">
      <FormFieldPhoneCountry v-model:country="country" />
      <input
        :id="field.name"
        v-model="number"
        class="input-text-phone"
        :name="field.name"
        type="text"
        @focus="blured = false"
        @blur="blured = true"
      />
      <FormButtonFormFieldButtonReset v-if="number" @click="reset" />
    </div>
    <FormErrorMessages :messages="feedback"></FormErrorMessages>
  </div>
</template>
<script setup lang="ts">
import {
  isValidPhoneNumber,
  AsYouType,
  parsePhoneNumber,
  type CountryCode,
  ParseError,
} from 'libphonenumber-js';
interface Props {
  field: FormField;
}

const {
  formFieldsValidation: { releaseFieldInvalidate, forceFieldInvalidate },
  checkValidatedFormOnMounted,
} = inject(INJECT_FORM_PAGE);

const debouncedNumber = useDebounceFn(() => {
  numberToTest.value = number.value;
}, 500);

const getCountryFromNumber = (nb) => {
  if (!nb) return 'FR';
  try {
    const phoneNumber = parsePhoneNumber(nb);
    const countries = phoneNumber.getPossibleCountries();
    return countries.length > 0 ? countries[0] : 'FR';
  } catch (e) {
    return 'FR';
  }
};

const formatInternational = (numberValue, countryValue) => {
  const formated = parsePhoneNumber(
    numberValue,
    countryValue
  ).formatInternational();
  fieldValue.value = formated || '';
};

const testIsValidNumber = (numberValue, countryValue) => {
  return isValidPhoneNumber(numberValue, countryValue);
};

const blured = ref<boolean>(false);

const props = defineProps({
  field: null
});

const {
  formFieldsData: { memberValues },
} = inject(INJECT_FORM_PAGE);

const fieldValue = computed({
  get() {
    return memberValues.value[props.field.name].value;
  },
  set(value) {
    memberValues.value[props.field.name].value = value;
  },
});

const label = computed(() => {
  return isFieldRequired(props.field)
    ? `${props.field.label}*`
    : props.field.label;
});

const number = ref(fieldValue.value);

const numberToTest = ref(fieldValue.value);

const { messages } = useFormFieldValidation(props.field, memberValues);

const isValid = ref<boolean>(true);

const country = ref<CountryCode>(getCountryFromNumber(fieldValue.value));

const phoneErrorMessage = computed(() => {
  return isValid.value || number.value === '' ? '' : 'Numéro non valide';
});

const messagesWithPhoneError = computed(() => {
  if (phoneErrorMessage.value)
    return [...messages.value, phoneErrorMessage.value];
  return [...messages.value];
});

const feedback = computed(() => {
  let feedbackMessages = [' '];
  if (messagesWithPhoneError.value.length > 0) {
    feedbackMessages = [...messagesWithPhoneError.value];
  }
  return feedbackMessages;
});

const reset = () => {
  fieldValue.value = '';
  number.value = '';
  numberToTest.value = '';
};

watch(
  () => numberToTest.value,
  (numberValue) => {
    isValid.value = testIsValidNumber(number.value, country.value);
    if (isValid.value) {
      formatInternational(numberValue, country.value);
      releaseFieldInvalidate(props.field.name);
    } else {
      fieldValue.value = '';
      if (numberValue.length === 0) {
        releaseFieldInvalidate(props.field.name);
      } else {
        forceFieldInvalidate(props.field.name);
      }
    }
  }
);

watch(
  () => country.value,
  (countryValue) => {
    if (number.value) {
      isValid.value = testIsValidNumber(number.value, country.value);
      if (!isValid.value) {
        number.value = '';
        fieldValue.value = '';
      } else {
        try {
          const phoneNumber = parsePhoneNumber(number.value);
          const countries = phoneNumber.getPossibleCountries();
          if (!countries.includes(countryValue)) {
            number.value = '';
            fieldValue.value = '';
          }
        } catch (error) {
          if (error instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
            number.value = '';
            fieldValue.value = '';
          } else {
            throw error;
          }
        }
      }
    }
  }
);

watch(
  () => number.value,
  (to) => {
    debouncedNumber();
  }
);
</script>
<style lang="scss" scoped>
.input-phone {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  input {
    padding-right: 3rem;
  }
}
</style>
